import React, { useContext } from "react";
import { HotelContext } from "../../context/HotelContext";
import { getLocale, translate } from "../../utils/Translation";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip, CartesianGrid } from "recharts";

const ActionsReviewsSections = ({
  loading,
  error,
  ratingDistribution,
  platformData,
  reviews,
  averageRating,
  totalReviews,
  maxRatingCount,
  renderStars,
}) => {
  const { lang } = useContext(HotelContext);
  const locale = getLocale(lang);

  return (
    <div className="actions-reviews__content">
      <div className="actions-reviews__summary" style={{ position: "relative" }}>
        <h2 className="actions-reviews__summary-title">{translate("v2_actions_reviews_summary_title", lang)}</h2>
        <p className="actions-reviews__summary-subtitle">{translate("v2_actions_reviews_summary_sub", lang)}</p>
        <div className="actions-reviews__summary-content">
          <div className="actions-reviews__rating-chart">
            {loading ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#707070" }}>
                {translate("v2_loading", lang)}
              </div>
            ) : ratingDistribution.length === 0 ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#707070" }}>
                {translate("v2_actions_reviews_no_data", lang)}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ratingDistribution} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <Tooltip
                    cursor={false}
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;
                      const { rating, count } = payload[0].payload;
                      return (
                        <div
                          style={{
                            backgroundColor: "white",
                            border: "1px solid #e0e0e0",
                            borderRadius: "3px",
                            padding: "4px 8px",
                            fontSize: "13px",
                            color: "#3C5544",
                            whiteSpace: "nowrap",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            pointerEvents: "none",
                          }}
                        >
                          {translate("v2_reviews_stars_tooltip", lang, { rating, count })}
                        </div>
                      );
                    }}
                  />
                  <XAxis type="number" domain={[0, maxRatingCount]} hide />
                  <YAxis dataKey="rating" type="category" tick={{ fontSize: 14, fill: "#707070" }} axisLine={false} tickLine={false} width={30} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={10} isAnimationActive={false} background={{ fill: "#f5f5f5", radius: [0, 4, 4, 0] }}>
                    {ratingDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill="#697F6A" />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="actions-reviews__rating-display">
            <div className="actions-reviews__rating-value">
              {loading
                ? "--"
                : (averageRating || 0).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
            </div>
            <div className="actions-reviews__stars">{renderStars(Math.floor(averageRating || 0))}</div>
            <div className="actions-reviews__review-count">
              {loading ? "--" : `${totalReviews || 0} ${translate("v2_reviews_count_label", lang)}`}
            </div>
          </div>
        </div>
      </div>

      <div className="actions-reviews__platforms">
        <h2 className="actions-reviews__platforms-title">{translate("v2_actions_reviews_platforms_title", lang)}</h2>
        <p className="actions-reviews__platforms-subtitle">{translate("v2_actions_reviews_platforms_sub", lang)}</p>
        <div className="actions-reviews__platforms-content">
          <div className="actions-reviews__platform-chart">
            {loading ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#707070" }}>
                {translate("v2_loading", lang)}
              </div>
            ) : platformData.length === 0 ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#707070" }}>
                {translate("v2_actions_reviews_no_data", lang)}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={platformData} margin={{ top: 35, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="0" vertical={false} horizontal={true} stroke="rgba(27, 36, 27, 0.4)" strokeWidth={1} />
                  <XAxis dataKey="name" tick={false} axisLine={true} tickLine={true} />
                  <YAxis
                    domain={[0, "dataMax"]}
                    ticks={[5, 10]}
                    tick={{ fontSize: 14, fill: "#707070" }}
                    axisLine={true}
                    tickLine={false}
                    label={{
                      value: translate("v2_reviews_platform_y_axis", lang),
                      angle: 0,
                      position: "top",
                      offset: 15,
                      style: { textAnchor: "middle", fontSize: "14px", fill: "#707070" },
                    }}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div style={{ backgroundColor: "white", border: "1px solid #e0e0e0", borderRadius: "3px", fontSize: "15px", padding: "8px 10px", color: "#3C5544" }}>
                            {translate("v2_reviews_platform_tooltip", lang, { name: data.name, count: data.count })}
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {platformData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="actions-reviews__platform-legend">
            {loading ? (
              <div style={{ color: "#707070" }}>{translate("v2_loading", lang)}</div>
            ) : platformData.length === 0 ? (
              <div style={{ color: "#707070" }}>{translate("v2_no_data", lang)}</div>
            ) : (
              platformData.map((platform, index) => (
                <div key={index} className="actions-reviews__platform-item">
                  <div
                    className={`actions-reviews__platform-dot actions-reviews__platform-dot--${platform.className}`}
                    style={{ backgroundColor: platform.color }}
                  ></div>
                  <span className="actions-reviews__platform-label">
                    {translate("v2_reviews_platform_legend", lang, {
                      name: platform.name,
                      count: platform.count,
                      percentage: platform.percentage,
                    })}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="actions-reviews__products">
        <h2 className="actions-reviews__products-title">{translate("v2_actions_reviews_products_title", lang)}</h2>
        <p className="actions-reviews__products-subtitle">{translate("v2_actions_reviews_products_sub", lang)}</p>
        {loading ? (
          <div style={{ padding: "20px", textAlign: "center", color: "#707070" }}>
            {translate("v2_actions_reviews_loading", lang)}
          </div>
        ) : error ? (
          <div style={{ padding: "20px", textAlign: "center", color: "#d32f2f" }}>
            {translate("v2_actions_reviews_error", lang)}
          </div>
        ) : reviews.length === 0 ? (
          <div style={{ padding: "20px", textAlign: "center", color: "#707070" }}>
            {translate("v2_actions_reviews_empty", lang)}
          </div>
        ) : (
          <div className="actions-reviews__products-grid">
            {reviews.map((review) => (
              <div key={review.id} className="actions-reviews__review-card">
                <div className="actions-reviews__review-header">
                  <div className="actions-reviews__review-avatar"></div>
                  <div className="actions-reviews__review-info">
                    <div className="actions-reviews__review-name">{review.name}</div>
                  </div>
                </div>
                <div className="actions-reviews__review-meta">
                  <div className="actions-reviews__review-stars">{renderStars(review.rating)}</div>
                  <span className="actions-reviews__review-date">{review.date}</span>
                </div>
                <div className="actions-reviews__review-text">{review.text}</div>
                <div className="actions-reviews__review-footer actions-reviews__review-footer--right">
                  <span className="actions-reviews__review-platform">{review.platform}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionsReviewsSections;
